/**
 * React Router v7 Route for AdsList page
 *
 * This replaces the getInitialProps pattern with RR7's loader.
 * The loader receives {context, request, params} from entry.server.tsx (SSR)
 * or from entry.client.tsx (client-side navigation).
 *
 * AdsList is the main listing page for vehicles (coches-segunda-mano, motos-segunda-mano, etc.)
 */

import {useLoaderData} from 'react-router-dom'

import {getNavigationState} from '../../helpers/navigationStateCache.ts'
import {withThirdPartiesParam} from '../../hocs/withThirdPartiesParam.js'
import {getInitialProps} from './getInitialProps.js'
import AdsListPageRaw from './index.js'

// coches.scss / motos.scss set the tenant's variables and import index.scss (tenantImportResolverPlugin)
import './styles/__TENANT__.scss'
// HOC wrapping that used to live in `./index.js`. Moved here so `./index.js`
// exports the raw component and receives Fast Refresh in dev.
const AdsListPage = withThirdPartiesParam(AdsListPageRaw)

/**
 * Loader arguments interface
 */
interface LoaderArgs {
  context: any
  request: Request
  params: Record<string, string | undefined>
}

/**
 * AdsList page data interface
 */
interface AdsListLoaderData {
  breadcrumb: any[]
  configuratorData: any
  configuratorEmptyCaseUrl?: string
  createdAlert?: any
  discardAd?: any
  error?: boolean
  faqsForUrlItems?: any[]
  faqsForUrlSectionTitle?: string
  filtersList: string[]
  h1: string
  hasNoMatchesUserAlertInformation?: boolean
  helmet: any
  initialResults: {
    items: any[]
    totalResults: number
    totalPages: number
    aggregations?: any
  }
  initialSearch: any
  isAdsListSearchPage: boolean
  isLoading?: boolean
  isMakeHiredDealer?: boolean
  lastFilter?: string
  listFiltersOptions: {
    [key: string]: {
      options: any[]
    }
  }
  listingSectionId: string
  loadThirdParties?: boolean
  makeModelFaqs?: any
  notifications: {
    [key: string]: boolean
  }
  pageErrors: {
    [key: string]: any
  }
  pageName: string
  paginationPlaceholderUrl: string
  recommendedAds?: {
    items: any[]
  }
  searchAiNotificationType?: string
  searchFaqs?: any[]
  showCardConfigurator?: boolean
  showNewVehicleFaq?: boolean
  showSortManagementNotification?: boolean
  structuredDataJsonList: any[]
  tagclouds: any[]
  trackingData: any
  visitedAdData: {
    id?: string
    isAccessible: boolean
    position: number
  }
}

/**
 * Loader function for AdsList page - replaces getInitialProps
 *
 * @param args - Loader arguments with context, request, and params
 * @returns Props for AdsList component
 */
export async function loader({context, request, params}: LoaderArgs): Promise<AdsListLoaderData> {
  // Extract route information from React Router v7 request
  const url = new URL(request.url)
  const pathname = url.pathname
  const search = url.search
  const query = Object.fromEntries(url.searchParams.entries())

  // Recovered from the navigation that brought the user here; RR7 keeps `state` out of
  // `LoaderFunctionArgs` (see helpers/navigationStateCache.ts).
  const state = getNavigationState(pathname)

  // Build routeInfo object compatible with getInitialProps
  const routeInfo = {
    location: {
      pathname,
      query,
      search,
      state
    },
    params
  }

  // Call the existing getInitialProps function with the adapted parameters
  const data = await getInitialProps({
    routeInfo,
    context
  })

  return data as AdsListLoaderData
}

/**
 * RR7 Component wrapper
 *
 * This wrapper uses useLoaderData() to get data from the loader
 * and passes it as props to the AdsList component.
 */
export function Component() {
  const data = useLoaderData<AdsListLoaderData>()

  // AdsListPage is the wrapped component (withThirdPartiesParam HOC applied)
  // The HOC will handle the loadThirdParties prop automatically via useLocation
  return <AdsListPage {...data} />
}
